/*
  assets/c-product-zoom.js
  商品写真の全画面拡大と商品画像一覧（作業管理 No.16・snippets/c-product-zoom.liquid）

  やっていることは3つ。
  1. メイン写真をタップ → 表示中の写真から全画面（PhotoSwipe）で開く
  2. 左下のボタン → 商品画像一覧を開く。一覧の写真をタップ → その写真から全画面で開く
  3. 全画面を閉じたら、最後に見ていた写真までメインスライダーを送る
     （スライダー → カラー選択の同期は main.js の slideChange に任せる）

  全画面の写真データは一覧の <a> から作る（一覧とスライダーは同じ product.media の順）。
*/
import PhotoSwipeLightbox from 'https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe-lightbox.esm.min.js';

const PSWP_MODULE = 'https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe.esm.min.js';
const PSWP_CSS = 'https://cdn.jsdelivr.net/npm/photoswipe@5.4.4/dist/photoswipe.css';

// 全画面のアイコン。形はスライダーの矢印 angle.svg（Iconly Light, 線 1.5px）に合わせる
const LINE = 'fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"';
const CHEVRON_LEFT = `<svg viewBox="0 0 8.5 15.5" width="8.5" height="15.5" ${LINE}><path d="M7.75.75l-7 7 7 7"/></svg>`;
const CHEVRON_RIGHT = `<svg viewBox="0 0 8.5 15.5" width="8.5" height="15.5" ${LINE}><path d="M.75.75l7 7-7 7"/></svg>`;
const ICON_CLOSE = `<svg viewBox="0 0 18 18" width="18" height="18" ${LINE}><path d="M.75.75l16.5 16.5M17.25.75.75 17.25"/></svg>`;
// 「＋」の縦線は拡大中に CSS で消して「－」にする（PhotoSwipe 既定と同じクラス名）
const ICON_ZOOM = `<svg viewBox="0 0 20 20" width="20" height="20" ${LINE}><circle cx="8.5" cy="8.5" r="7.75"/><path d="M14.25 14.25l5 5M5.5 8.5h6"/><path class="pswp__zoom-icn-bar-v" d="M8.5 5.5v6"/></svg>`;

function pswpIcon(modifier, svg) {
  return `<span class="c-product-zoom-pswp-icon c-product-zoom-pswp-icon--${modifier}" aria-hidden="true">${svg}</span>`;
}

const root = document.querySelector('[data-product-zoom]');
const sliderEl = document.querySelector('.js-product-single-main-slider');

if (root && sliderEl) init();

function init() {
  const grid = root.querySelector('[data-product-zoom-grid]');
  const items = Array.from(grid.querySelectorAll('[data-product-zoom-item]'));

  // 商品セクション側の stacking context に閉じ込められないよう body 直下へ出す
  document.body.appendChild(grid);

  const lightbox = new PhotoSwipeLightbox({
    dataSource: items.map((a) => ({
      src: a.href,
      width: Number(a.dataset.pswpWidth),
      height: Number(a.dataset.pswpHeight),
      alt: a.querySelector('img')?.alt || '',
    })),
    pswpModule: () => {
      loadCss();
      return import(PSWP_MODULE);
    },
    bgOpacity: 1,
    showHideAnimationType: 'fade',
    // PhotoSwipe の倍率は「元画像の実寸」基準（数値 4 だと 2000px 画像を 8000px まで＝SP で約20倍）。
    // 「画面に収まった大きさの最大4倍」にするため fit 基準で渡す。
    // ダブルタップ時の倍率は既定（fit の3倍・元画像の実寸まで）のまま
    maxZoomLevel: (zoomLevel) => zoomLevel.fit * 4,
    arrowPrevTitle: '前の写真',
    arrowNextTitle: '次の写真',
    closeTitle: '閉じる',
    zoomTitle: '拡大',
    errorMsg: '画像を読み込めませんでした',
    // アイコンはサイトの線アイコン（線 1.5px・端は丸め）に揃える。見た目は c-product-zoom.css
    arrowPrevSVG: pswpIcon('arrow', CHEVRON_LEFT),
    arrowNextSVG: pswpIcon('arrow', CHEVRON_RIGHT),
    closeSVG: pswpIcon('close', ICON_CLOSE),
    zoomSVG: pswpIcon('zoom', ICON_ZOOM),
  });

  lightbox.on('close', () => {
    const swiper = sliderEl.swiper;
    const index = lightbox.pswp.currIndex;
    if (swiper && swiper.realIndex !== index) swiper.slideToLoop(index, 0);
  });

  lightbox.init();

  // --- 1. メイン写真のタップ ----------------------------------------
  // Swiper の click イベントはドラッグ（スワイプ）では発火しない＝タップだけ拾える。
  // Swiper は main.js（jQuery ready）で作られるので、できるまで待ってから繋ぐ
  whenSwiperReady((swiper) => {
    sliderEl.classList.add('is-zoomable');
    swiper.on('click', (_swiper, event) => {
      if (event.target.closest('button')) return; // 前後の矢印
      lightbox.loadAndOpen(swiper.realIndex);
    });
  });

  // --- 2. 商品画像一覧 ------------------------------------------------
  const openButton = root.querySelector('[data-product-zoom-open-grid]');
  const closeButton = grid.querySelector('[data-product-zoom-close-grid]');

  const openGrid = () => {
    grid.hidden = false;
    grid.scrollTop = 0; // 前回写真を選んだ位置のまま開かないよう先頭に戻す
    document.documentElement.classList.add('is-product-zoom-grid-open');
    closeButton.focus();
  };
  const closeGrid = () => {
    grid.hidden = true;
    document.documentElement.classList.remove('is-product-zoom-grid-open');
    openButton.focus();
  };

  openButton.addEventListener('click', openGrid);
  closeButton.addEventListener('click', closeGrid);

  items.forEach((a, index) => {
    a.addEventListener('click', (event) => {
      event.preventDefault();
      lightbox.loadAndOpen(index);
    });
  });

  // Esc: 全画面が開いていればそちら（PhotoSwipe 側）が閉じる。一覧だけのときは一覧を閉じる
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || grid.hidden || lightbox.pswp) return;
    closeGrid();
  });
}

function whenSwiperReady(callback, tries = 0) {
  if (sliderEl.swiper) return callback(sliderEl.swiper);
  if (tries > 100) return; // 10秒待って作られなければ諦める（全画面は一覧からは開ける）
  setTimeout(() => whenSwiperReady(callback, tries + 1), 100);
}

function loadCss() {
  if (document.querySelector(`link[href="${PSWP_CSS}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = PSWP_CSS;
  document.head.appendChild(link);
}
